import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { api } from '../../lib/api';
import Spinner from '../../components/ui/Spinner';
import { Select, Button, Input } from '../../components/ui/Kit';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  ROOM_COLORS,
  formatTalkTime,
  targetBadgeStatus,
  DEFAULT_GOALS,
} from '../../lib/roomTypeMetrics';
import { addWeeksToMonday, formatWeekRangeLabel } from '../../lib/kpiTrackerUtils';

const { FiChevronLeft, FiChevronRight, FiClock, FiTarget, FiTrendingUp, FiPhone, FiUsers } = FiIcons;

const MANAGER_ROLES = new Set(['manager', 'admin', 'super_admin']);

function TargetBadge({ actual, target, formatValue, isSeconds }) {
  const status = targetBadgeStatus(actual, target, { isSeconds });
  const colors = {
    good: 'bg-emerald-100 text-emerald-800',
    warn: 'bg-amber-100 text-amber-800',
    bad: 'bg-red-100 text-red-800',
    neutral: 'bg-gray-100 text-gray-600',
  };
  const label = formatValue ? formatValue(target) : `${target}%`;
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${colors[status]}`}>
      Target {label}
    </span>
  );
}

function Sparkline({ data, color = '#00AAFF' }) {
  const chartData = (data ?? []).map((v, i) => ({ i, v }));
  if (!chartData.length) return <div className="h-10" />;
  return (
    <ResponsiveContainer width="100%" height={40}>
      <LineChart data={chartData} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
        <Line type="monotone" dataKey="v" stroke={color} strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function KpiCard({ title, icon, value, valueLabel, target, targetFormat, sparkData, sparkColor, children }) {
  const Icon = icon;
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <SafeIcon icon={Icon} className="text-gray-400 w-4 h-4" />
          <span className="text-sm font-medium text-gray-600">{title}</span>
        </div>
        {target != null ? (
          <TargetBadge actual={value} target={target} formatValue={targetFormat} isSeconds={!!targetFormat} />
        ) : null}
      </div>
      <p className="text-2xl font-bold text-gray-900 tabular-nums">{valueLabel}</p>
      {children}
      <Sparkline data={sparkData} color={sparkColor} />
    </div>
  );
}

function DistributionBar({ pct }) {
  const rb = pct?.RB ?? 0;
  const ex = pct?.EX ?? 0;
  const sk = pct?.SK ?? 0;
  if (rb + ex + sk === 0) {
    return (
      <div className="h-8 rounded-lg bg-gray-100 text-xs text-gray-400 flex items-center justify-center">
        No classified calls this week
      </div>
    );
  }
  return (
    <div className="h-8 rounded-lg overflow-hidden flex text-[10px] font-semibold text-white">
      {rb > 0 ? (
        <div style={{ width: `${rb}%`, backgroundColor: ROOM_COLORS.RB }} className="flex items-center justify-center">
          {rb}%
        </div>
      ) : null}
      {ex > 0 ? (
        <div style={{ width: `${ex}%`, backgroundColor: ROOM_COLORS.EX }} className="flex items-center justify-center">
          {ex}%
        </div>
      ) : null}
      {sk > 0 ? (
        <div style={{ width: `${sk}%`, backgroundColor: ROOM_COLORS.SK }} className="flex items-center justify-center">
          {sk}%
        </div>
      ) : null}
    </div>
  );
}

export default function ReadTheRoomSection({
  user,
  weekStart,
  setWeekStart,
  selectedAgentId,
  setSelectedAgentId,
  isManager,
  agentOptions,
  viewingAllAgents,
  channelFilter = '',
}) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [distribution, setDistribution] = useState(null);
  const [fourWeekTrend, setFourWeekTrend] = useState([]);
  const [bestTimes, setBestTimes] = useState(null);
  const [flags, setFlags] = useState([]);
  const [goals, setGoals] = useState(DEFAULT_GOALS);
  const [goalForm, setGoalForm] = useState(DEFAULT_GOALS);
  const [goalsSaving, setGoalsSaving] = useState(false);
  const [agencyDefaultGoals, setAgencyDefaultGoals] = useState(false);

  const effectiveAgentId =
    selectedAgentId === 'self' || selectedAgentId === '__all__' ? user?.id : selectedAgentId;

  const canEditGoals =
    !viewingAllAgents && (effectiveAgentId === user?.id || MANAGER_ROLES.has(user?.role));

  const canSetAgencyDefaults =
    MANAGER_ROLES.has(user?.role) && ['admin', 'super_admin'].includes(user?.role);

  const loadData = useCallback(async () => {
    if (!user?.agency_id || !effectiveAgentId || viewingAllAgents) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const channel = channelFilter || null;
      const roomOpts = { channel };
      const [weekStats, dist, trend, times, coachingFlags, goalsRow] = await Promise.all([
        api.roomType.getWeekStats(effectiveAgentId, user.agency_id, weekStart, roomOpts),
        api.roomType.getTypeDistribution(effectiveAgentId, user.agency_id, weekStart, roomOpts),
        api.roomType.getFourWeekTrend(effectiveAgentId, user.agency_id, weekStart, roomOpts),
        api.roomType.getBestCallTimes(effectiveAgentId, user.agency_id, weekStart, roomOpts),
        api.roomType.getCoachingFlags(effectiveAgentId, user.agency_id, weekStart, roomOpts),
        api.roomType.getGoals(effectiveAgentId, user.agency_id),
      ]);
      setStats(weekStats);
      setDistribution(dist);
      setFourWeekTrend(trend);
      setBestTimes(times);
      setFlags(coachingFlags);
      setGoals(goalsRow);
      setGoalForm({
        talk_time_target_seconds: goalsRow.talk_time_target_seconds ?? 120,
        first_call_close_target_pct: goalsRow.first_call_close_target_pct ?? 60,
        callback_show_target_pct: goalsRow.callback_show_target_pct ?? 40,
        skeptic_save_target_pct: goalsRow.skeptic_save_target_pct ?? 35,
        rb_conversion_target_pct: goalsRow.rb_conversion_target_pct ?? 60,
        ex_reengage_target_pct: goalsRow.ex_reengage_target_pct ?? 40,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user?.agency_id, effectiveAgentId, weekStart, viewingAllAgents, channelFilter]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const weekly = stats?.weekly ?? {};
  const spark = stats?.sparklines ?? {};

  const trendChartData = useMemo(
    () =>
      (fourWeekTrend ?? []).map((w) => ({
        label: format(parseISO(`${w.weekStart}T12:00:00`), 'M/d'),
        RB: w.RB,
        EX: w.EX,
        SK: w.SK,
      })),
    [fourWeekTrend],
  );

  const heatmapByType = useMemo(() => {
    const hours = bestTimes?.hours ?? [];
    const byRt = { RB: {}, EX: {}, SK: {} };
    for (const row of bestTimes?.rows ?? []) {
      byRt[row.roomType][row.hour] = row;
    }
    return { hours, byRt, bestHourByType: bestTimes?.bestHourByType ?? {} };
  }, [bestTimes]);

  const handleSaveGoals = async () => {
    if (!user?.agency_id) return;
    setGoalsSaving(true);
    try {
      await api.roomType.setGoals(effectiveAgentId, user.agency_id, goalForm, {
        agencyDefault: agencyDefaultGoals && canSetAgencyDefaults,
      });
      await loadData();
    } catch (err) {
      console.error(err);
    } finally {
      setGoalsSaving(false);
    }
  };

  if (viewingAllAgents) return null;

  return (
    <section className="space-y-6 pt-4 border-t border-gray-200">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-xl font-bold text-gray-900">Read the Room</h2>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          {isManager ? (
            <Select
              value={selectedAgentId}
              onChange={(e) => setSelectedAgentId(e.target.value)}
              className="min-w-[180px]"
              options={agentOptions}
            />
          ) : null}
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-2 py-1.5 shadow-sm">
            <button
              type="button"
              onClick={() => setWeekStart((w) => addWeeksToMonday(w, -1))}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-600"
              aria-label="Previous week"
            >
              <SafeIcon icon={FiChevronLeft} />
            </button>
            <span className="text-sm font-medium text-gray-800 whitespace-nowrap px-2">
              {formatWeekRangeLabel(weekStart)}
            </span>
            <button
              type="button"
              onClick={() => setWeekStart((w) => addWeeksToMonday(w, 1))}
              className="p-2 rounded-md hover:bg-gray-100 text-gray-600"
              aria-label="Next week"
            >
              <SafeIcon icon={FiChevronRight} />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" className="text-primary" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <KpiCard
              title="Talk Time"
              icon={FiClock}
              value={weekly.talkTimeAvg}
              valueLabel={formatTalkTime(weekly.talkTimeAvg)}
              target={goals.talk_time_target_seconds}
              targetFormat={(t) => formatTalkTime(t)}
              sparkData={spark.talkTimeAvg}
              sparkColor={ROOM_COLORS.RB}
            />
            <KpiCard
              title="First-Call Close Rate"
              icon={FiTarget}
              value={weekly.firstCallClosePct}
              valueLabel={`${weekly.firstCallClosePct ?? 0}%`}
              target={goals.first_call_close_target_pct}
              sparkData={spark.firstCallClosePct}
              sparkColor="#00AAFF"
            />
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiTrendingUp} className="text-gray-400 w-4 h-4" />
                <span className="text-sm font-medium text-gray-600">Conversion by Type</span>
              </div>
              <div className="space-y-2 text-sm">
                {[
                  { key: 'RB', label: 'RB close', pct: weekly.rbConversionPct, color: ROOM_COLORS.RB },
                  { key: 'EX', label: 'EX re-engage', pct: weekly.exReengagePct, color: ROOM_COLORS.EX },
                  { key: 'SK', label: 'SK save', pct: weekly.skepticSavePct, color: ROOM_COLORS.SK },
                ].map((row) => (
                  <div key={row.key}>
                    <div className="flex justify-between text-xs text-gray-500 mb-0.5">
                      <span>{row.label}</span>
                      <span className="font-semibold text-gray-800">{row.pct ?? 0}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${Math.min(100, row.pct ?? 0)}%`, backgroundColor: row.color }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <KpiCard
              title="Callback Show Rate"
              icon={FiPhone}
              value={weekly.callbackShowPct}
              valueLabel={`${weekly.callbackShowPct ?? 0}%`}
              target={goals.callback_show_target_pct}
              sparkData={spark.callbackShowPct}
              sparkColor={ROOM_COLORS.EX}
            />
            <KpiCard
              title="Skeptic Save Rate"
              icon={FiUsers}
              value={weekly.skepticSavePct}
              valueLabel={`${weekly.skepticSavePct ?? 0}%`}
              target={goals.skeptic_save_target_pct}
              sparkData={spark.skepticSavePct}
              sparkColor={ROOM_COLORS.SK}
            />
            <KpiCard
              title="Re-engagement Rate"
              icon={FiTrendingUp}
              value={weekly.exReengagePct}
              valueLabel={`${weekly.exReengagePct ?? 0}%`}
              target={goals.ex_reengage_target_pct}
              sparkData={spark.exReengagePct}
              sparkColor={ROOM_COLORS.EX}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 space-y-4">
              <h3 className="font-semibold text-gray-900">Type Distribution</h3>
              <DistributionBar pct={distribution?.pct} />
              <div className="flex gap-4 text-xs text-gray-500">
                <span>
                  <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: ROOM_COLORS.RB }} />
                  RB
                </span>
                <span>
                  <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: ROOM_COLORS.EX }} />
                  EX
                </span>
                <span>
                  <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: ROOM_COLORS.SK }} />
                  SK
                </span>
              </div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trendChartData}>
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="RB" stackId="a" fill={ROOM_COLORS.RB} />
                    <Bar dataKey="EX" stackId="a" fill={ROOM_COLORS.EX} />
                    <Bar dataKey="SK" stackId="a" fill={ROOM_COLORS.SK} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 overflow-x-auto">
              <h3 className="font-semibold text-gray-900 mb-3">Best Time to Call by Type</h3>
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-2 pr-2 text-gray-500">Hour</th>
                    {['RB', 'EX', 'SK'].map((rt) => (
                      <th key={rt} className="text-center py-2 px-1 font-semibold" style={{ color: ROOM_COLORS[rt] }}>
                        {rt}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(heatmapByType.hours ?? []).map((h) => (
                    <tr key={h} className="border-b border-gray-50">
                      <td className="py-1.5 text-gray-600">
                        {h === 12 ? '12pm' : h < 12 ? `${h}am` : `${h - 12}pm`}
                      </td>
                      {['RB', 'EX', 'SK'].map((rt) => {
                        const cell = heatmapByType.byRt[rt]?.[h];
                        const isBest = heatmapByType.bestHourByType[rt] === h;
                        return (
                          <td
                            key={rt}
                            className={`text-center py-1.5 px-1 tabular-nums ${
                              isBest ? 'bg-emerald-50 font-semibold text-emerald-800' : 'text-gray-600'
                            }`}
                          >
                            {cell?.calls ? `${cell.rate ?? 0}%` : '—'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
              <h3 className="font-semibold text-gray-900 mb-3">Coaching Flags</h3>
              {flags.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">No flags this week — keep it up!</p>
              ) : (
                <ul className="space-y-2">
                  {flags.map((f, i) => (
                    <li
                      key={i}
                      className={`text-sm rounded-lg px-3 py-2 flex gap-2 ${
                        f.type === 'warning' ? 'bg-amber-50 text-amber-900' : 'bg-emerald-50 text-emerald-900'
                      }`}
                    >
                      <span>{f.type === 'warning' ? '🚩' : '✅'}</span>
                      <span>{f.message}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {canEditGoals ? (
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
                <h3 className="font-semibold text-gray-900 mb-3">Goal Settings</h3>
                {canSetAgencyDefaults ? (
                  <label className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                    <input
                      type="checkbox"
                      checked={agencyDefaultGoals}
                      onChange={(e) => setAgencyDefaultGoals(e.target.checked)}
                    />
                    Set as agency-wide default
                  </label>
                ) : null}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Input
                    label="Talk time (sec)"
                    type="number"
                    min={0}
                    value={goalForm.talk_time_target_seconds}
                    onChange={(e) =>
                      setGoalForm((g) => ({ ...g, talk_time_target_seconds: e.target.value }))
                    }
                  />
                  <Input
                    label="First-call close %"
                    type="number"
                    min={0}
                    max={100}
                    value={goalForm.first_call_close_target_pct}
                    onChange={(e) =>
                      setGoalForm((g) => ({ ...g, first_call_close_target_pct: e.target.value }))
                    }
                  />
                  <Input
                    label="Callback show %"
                    type="number"
                    min={0}
                    max={100}
                    value={goalForm.callback_show_target_pct}
                    onChange={(e) =>
                      setGoalForm((g) => ({ ...g, callback_show_target_pct: e.target.value }))
                    }
                  />
                  <Input
                    label="Skeptic save %"
                    type="number"
                    min={0}
                    max={100}
                    value={goalForm.skeptic_save_target_pct}
                    onChange={(e) =>
                      setGoalForm((g) => ({ ...g, skeptic_save_target_pct: e.target.value }))
                    }
                  />
                  <Input
                    label="RB conversion %"
                    type="number"
                    min={0}
                    max={100}
                    value={goalForm.rb_conversion_target_pct}
                    onChange={(e) =>
                      setGoalForm((g) => ({ ...g, rb_conversion_target_pct: e.target.value }))
                    }
                  />
                  <Input
                    label="EX re-engage %"
                    type="number"
                    min={0}
                    max={100}
                    value={goalForm.ex_reengage_target_pct}
                    onChange={(e) =>
                      setGoalForm((g) => ({ ...g, ex_reengage_target_pct: e.target.value }))
                    }
                  />
                </div>
                <Button
                  className="mt-4"
                  onClick={() => void handleSaveGoals()}
                  isLoading={goalsSaving}
                  disabled={goalsSaving}
                >
                  Save Goals
                </Button>
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 flex items-center justify-center text-sm text-gray-400">
                Goal settings are only visible to the agent and managers.
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}
